import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import useGoBack from "@/hooks/useGoBack";
import useStateHandler from "@/hooks/useStateHandler";
import useTranslations from '@/i18n/useTranslations';
import GalleryService from "@/services/gallery.service";
import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import AudienceSelectionPopup from '@/shared-components/forms/add-board-content/shared-components/AudienceSelectionPopup';
import CalendarIcon from '@/shared-components/icons/components/forms/CalendarIcon';
import PasswordEyeOpen from '@/shared-components/icons/components/forms/PasswordEyeOpen';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import Loader from '@/shared-components/loader/Loader';
import Utils from '@/utils';
import { buildPublicContentPath } from '@/utils/contentRoutes';

import {
  applyPublicationAction,
  PUBLICATION_ACTIONS,
} from '../../publicationLifecycle';
import { getArticlePayload, hasMeaningfulPostText } from '../../saveFunctions';

import PublicationDatePopup from "./components/PublicationDatePopup";
import SaveArticleDraft from "./components/SaveArticleDraft";
import SaveAsDraftDialog from "./components/SaveAsDraftDialog";

import "./index.css";

export default function Header({
  formState,
  setFormState,
  formError,
  setFormError,
  editMode,
  previewHandler,
  openPublicationDate = false,
  setOpenPublicationDate = () => {},
  previewMode = false,
  fetchData = {},
  membersAudienceEnabled = false
}) {
  const t = useTranslations('common.forms.board_editor');

  const { cacheHandler, stateHandler } = useStateHandler();
  const navigate         = useNavigate();
  const goBack           = useGoBack("/profile/board");
  const initialState     = useRef(formState);

  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [openAudienceSelection, setOpenAudienceSelection] = useState(false);
  
  // ─── ① figure out when the article has all required parts ───
  const hasTitle = Boolean(formState.post_title?.trim().length);
  const hasCover = Boolean(formState.post_cover);      // cover image must be set
  const hasDesc  = Boolean(formState.post_description?.trim().length);

  // we get the structure to ensure that we have a body text
  const tempPayload = getArticlePayload(formState);

  const hasText     = hasMeaningfulPostText(tempPayload.post_texts);
  const hasPictures = (formState.post_pictures || []).length > 0;
  const hasArt      = (formState.post_artworks || []).length > 0;
  const hasVideos   = (formState.post_videos || []).length > 0;
  const hasPolls    = (formState.post_polls || []).length > 0;
  const hasQuizzes  = (formState.post_quizzes || []).length > 0;

  // now “body” means any of those:
  const hasBody     = hasText || hasPictures || hasArt || hasVideos || hasPolls || hasQuizzes;

  // publish enabled only once everything is present
  const canPublish  = hasTitle && hasCover && hasDesc && hasBody;
  const hasScheduledPublication = Boolean(formState.publicationDate && formState.publicationTime);
  const isCalendarActive = hasScheduledPublication;

  // detect whether the user has modified anything
  const hasChanges =
    JSON.stringify(formState) !== JSON.stringify(initialState.current);

  async function savePost({ asDraft = false, audience, exitAfterDraft = false } = {}) {
    setLoading(true);
    setFormError(null);

    // TODO: Pablo, show an error?
    // This is when they are uploading the cover picture
    // and still in process
    if (Utils.isPostMediaUploading(formState)) { 
      setLoading(false);
      return { blocked: 'uploading' };
    }
    if (!asDraft && Utils.isPostVideoProcessing(formState)) {
      setLoading(false);
      stateHandler.set('temporalPopup', {
        text: t('video_quality_processing'),
        variant: 'error',
      });
      return { blocked: 'video-processing' };
    }

    const payload = applyPublicationAction(
      getArticlePayload(formState),
      asDraft ? PUBLICATION_ACTIONS.SAVE_DRAFT : PUBLICATION_ACTIONS.PUBLISH
    );

    if (!asDraft) {
      payload.audience = audience;
    }

    const { response, data } = await GalleryService.submitPost(payload, true);
    setLoading(false);

    if (response.ok) {
      // TODO: Check if it is needed
      cacheHandler.triggerAction("SUBMIT_POST");
      cacheHandler.storeInCache(`post_${data._id}_view`, data);
      // if not a draft go to article view
      if(!asDraft) {
        navigate(
          buildPublicContentPath(data, 'article'),
          {
            replace: true,
            state: { }
          }
        );
      // when it is a draft we go to edit page
      } else if (!exitAfterDraft) {
        navigate(
          `/profile/edit-article/${data._id}`, 
          {
            replace: true,
            state: { }
          }
        );
      }
    } else if (response.status === 400) {
      setFormError({
        element: data.error_data.element,
        error_code: data.error_type
      });
    } else if (data?.error_type === 'VIDEO_PROCESSING_INCOMPLETE') {
      stateHandler.set('temporalPopup', {
        text: t('video_quality_processing'),
        variant: 'error',
      });
    }

    return { response, data };
  }

  const handlePublish = () => {
    if (membersAudienceEnabled) {
      setOpenAudienceSelection(true);
      return;
    }

    savePost({ audience: 'public' });
  };

  const handleAudienceSelect = audience => {
    savePost({ audience });
  };

  return (
    <>
      <div className={`board-header ${previewMode ? 'none' : ''}`}>
        {/* ——— Left controls: X + inline SaveArticleDraft ——— */}
        <div className="board-header__left-options">
          <XIcon
            onClick={() => {
              if (hasChanges && !editMode) {
                setOpenDialog(true);
              } else {
                goBack();
              }
            }}
          />

          {/*
            Show the inline SaveArticleDraft whenever the article
            is *not* yet published (post_status !== "published").
            This covers both “new” and “draft edit” flows.
          */}
          {/* Inline Save button — only for new articles or incomplete drafts */}
          {(
            !editMode 
             || (editMode && fetchData.post_status === "incomplete")
          ) && (
            <SaveArticleDraft
              formState={formState}
              savePost={() => savePost({ asDraft: true })}
              initialStateRef={initialState}
            />
          )}
        </div>

        {/* ——— Centered Title ——— */}
        <h1 className="board-header__heading">
          {editMode ? t('edit_article') : t('article')}
        </h1>

        {/* ——— Right controls: Preview + Post ——— */}
        <div className="board-header__right-options">
          <button
            type="button"
            className={`board-header__calendar-button board-header__calendar-button--desktop${isCalendarActive ? ' board-header__calendar-button--active' : ''}`}
            onClick={() => setOpenPublicationDate(true)}
            aria-label={t('schedule_publication')}
            aria-pressed={isCalendarActive}
          >
            <CalendarIcon className="board-header__calendar-icon" />
          </button>
          <span
            className="board-header__item board-header__item--preview"
            onClick={previewHandler}
            aria-label={t('preview')}
          >
            <PasswordEyeOpen className="board-header__preview-eye" />
          </span>

          <ContinueButton
            turnOff={!canPublish}
            loading={loading}
            className="board-header__item publish element-non-selectable board-header__continue-small"
            onClick={handlePublish}
          >
            {t('publish')}
            {loading && (
              <Loader inline
                className="board-header__spinner"
                thin
              />
            )}
          </ContinueButton>
        </div>
      </div>

      {/* ——— Confirmation dialog on Close (X) ——— */}
      {!editMode && (
        <SaveAsDraftDialog
          formState={formState}
          open={openDialog}
          close={() => setOpenDialog(false)}
          mode ="article"
          onSaveDraft={() => savePost({ asDraft: true, exitAfterDraft: true })}
        />
      )}

      <PublicationDatePopup
        open={openPublicationDate}
        onClose={() => setOpenPublicationDate(false)}
        formState={formState}
        setFormState={setFormState}
        formError={formError}
        setFormError={setFormError}
      />

      <AudienceSelectionPopup
        open={openAudienceSelection}
        onClose={() => setOpenAudienceSelection(false)}
        onSelect={handleAudienceSelect}
      />
    </>
  );
}
